import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MenubarModule } from 'primeng/menubar';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { getState } from '@ngrx/signals';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { StoryStore } from '../../../stores/story/story.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Bookmark } from '../../../models/bookmark';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MenuModule } from 'primeng/menu';
import { SidebarModule } from 'primeng/sidebar';
import { HeaderComponent } from '../../atoms/header/header.component';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { GridStore } from '../../../stores/grid/grid.state';
import { CustomGridStackWidget } from '../../../services/grid-widget-config.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import {
  ThemeCssClass,
  ThemeSwitcherComponent,
} from '../../atoms/theme-switcher/theme-switcher.component';
import { AtlasSearchComponent } from '../atlas-search/atlas-search.component';
import { FormsModule } from '@angular/forms';
import { Story } from '../../../models/story';
import { GlobalsStore } from '../../../stores/globals.state';
import { StyleClassModule } from 'primeng/styleclass';
import { Commune } from '../../../models/commune';
import { AvatarModule } from 'primeng/avatar';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { ActiveUserAvatarComponent } from '../../atoms/active-user-avatar/active-user-avatar.component';
import { TopicStore } from '../../../stores/topic/topic.state';
import { Topic } from '../../../models/topic';
import { Ripple } from 'primeng/ripple';
import { DialogModule } from 'primeng/dialog';
import { NgClass } from '@angular/common';
import { GridWidgetConfigService } from '../../../services/grid-widget-config.service';

@Component({
  selector: 'app-bookmark-menu',
  standalone: true,
  imports: [
    MenubarModule,
    SplitButtonModule,
    MenuModule,
    SidebarModule,
    HeaderComponent,
    ConfirmPopupModule,
    ConfirmDialogModule,
    ThemeSwitcherComponent,
    AtlasSearchComponent,
    FormsModule,
    StyleClassModule,
    AvatarModule,
    ActiveUserAvatarComponent,
    Ripple,
    DialogModule,
    NgClass,
  ],
  templateUrl: './bookmark-menu.component.html',
  styleUrl: './bookmark-menu.component.scss',
})
export class BookmarkMenuComponent implements OnChanges, OnInit {
  readonly activeUserStore = inject(ActiveUserStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);
  readonly storyStore = inject(StoryStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly widgetStore = inject(WidgetStore);
  readonly gridStore = inject(GridStore);
  readonly globalStore = inject(GlobalsStore);
  readonly topicStore = inject(TopicStore);
  readonly configService = inject(GridWidgetConfigService);

  @Output() loadLayout: EventEmitter<CustomGridStackWidget[]> =
    new EventEmitter();

  @Input() activeTopic: Topic | null = null;

  menuBarItems: MenuItem[] = [];
  topItems: MenuItem[] = [];
  miscItems: MenuItem[] = [];
  topicItems: MenuItem[] = [];
  bookmarkCategories: BookmarkCategory[] = [];
  groupedBookmarks: Record<string, Bookmark[]> = {};
  groupedStories: Record<string, Story[]> = {};
  activeCommune: Commune | null = null;
  activeBookmarkId: string | null = null;
  infoVisible = false;

  constructor(private confirmationDialogService: ConfirmationDialogService) {
    effect(() => {
      const globalState = getState(this.globalStore);
      const gridState = getState(this.gridStore);
      const bookmarkState = getState(this.bookmarkStore);

      if (this.activeBookmarkId !== gridState.activeBookmark?._id) {
        this.activeBookmarkId = gridState.activeBookmark?._id || null;
        setTimeout(() => {
          this.initializeBookmarkButton();
        }, 100);
      }
      if (!this.activeCommune) {
        this.activeCommune = globalState.activeCommune;
        this.setActiveBookmarkCategories();
      }
      if (
        bookmarkState.trigger &&
        bookmarkState.trigger.includes('bookmarkAdded')
      ) {
        queueMicrotask(() => {
          this.initializeMenu();
        });
      }
    });
  }

  /**
   * Initializes the component and sets the initial state or configurations required during the component's lifecycle.
   * Specifically, it ensures that the active bookmark categories are set.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    this.setActiveBookmarkCategories();
  }

  /**
   * Handles changes to bound input properties of a component. Triggered automatically
   * by Angular when any of the `@Input` bindings of the component are changed.
   *
   * @param {SimpleChanges} changes - An object of changes to the input bindings, where each key
   * corresponds to an input property name and its value is a SimpleChange object.
   * @return {void} This method does not return any value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTopic']) {
      this.setActiveBookmarkCategories();
    }
  }

  /**
   * Updates and sets the active bookmark categories based on the current active topic
   * or active commune configuration. If no specific configuration is provided, it falls
   * back to using all bookmark categories available in the store.
   *
   * @return {void} This method does not return any value.
   */
  private setActiveBookmarkCategories(): void {
    if (this.activeTopic && this.activeTopic.bookmarkCategories) {
      this.bookmarkCategories =
        this.bookmarkCategoryStore.getByIds(
          this.activeTopic.bookmarkCategories
        ) || [];
      this.topicItems = this.getTopicItems();
    } else if (this.activeCommune?.atlasConfig?.navigation?.length) {
      this.bookmarkCategories = this.bookmarkCategoryStore.getByIds(
        this.activeCommune.atlasConfig.navigation
      );
      this.topicItems = [];
    } else {
      this.bookmarkCategories = this.bookmarkCategoryStore.asArray();
      this.topicItems = [];
    }

    this.initializeMenu();
    this.initializeTopMenu();
  }

  /**
   * Initializes the menu with items based on bookmark categories and their respective children.
   * Iterates through bookmark categories, generates child menu items for each category,
   * and constructs the menu bar items array with the relevant data.
   *
   * @return {void} Does not return a value.
   */
  initializeMenu(): void {
    this.groupedBookmarks = this.bookmarkStore.groupedByCategory();
    this.groupedStories = this.storyStore.groupedByCategory();
    this.menuBarItems = [];
    this.bookmarkCategories.forEach(bookmarkCategory => {
      const categoryChildren = this.addMenuChildren(bookmarkCategory._id);
      if (categoryChildren.length > 0) {
        this.menuBarItems.push({
          pTooltip: bookmarkCategory.title,
          label: bookmarkCategory.title,
          icon: bookmarkCategory.private ? 'fas fa-eye-slash' : '',
          items: categoryChildren,
        });
      }
    });
  }

  /**
   * Gets the list of topic menu items.
   *
   * @return {MenuItem[]} An array of menu items representing the topics, each containing properties such as icon, command, and label.
   */
  private getTopicItems(): MenuItem[] {
    const topics = this.topicStore.sorted();
    return topics.map((topic: Topic) => {
      return {
        icon: topic.icon,
        command: event => {
          this.globalStore.triggerAtlasEvent('loadTopic', {
            topic: topic,
          });
        },
        label: topic.title,
      };
    });
  }

  /**
   * Initializes the bookmark button by setting up its related menu items, icons, and commands.
   * The menu includes options for saving bookmarks as a copy, overwriting the current view, and creating a new view.
   *
   * @return {void} Does not return a value.
   */
  private initializeBookmarkButton(): void {
    this.topItems = [
      {
        icon: 'fas fa-floppy-disk',
        items: [
          {
            disabled: !this.activeBookmarkId,
            icon: 'fas fa-copy',
            label: 'als Kopie speichern',
            command: event => {
              const activeBookmark = this.gridStore.activeBookmark();
              if (activeBookmark) {
                const copy: Bookmark = { ...activeBookmark };
                copy._id = '';
                this.showBookmarkForm(copy);
              }
            },
          },
          {
            disabled: !this.activeBookmarkId,
            icon: 'fas fa-pen',
            label: 'aktuelle Ansicht überschreiben',
            command: event =>
              this.showBookmarkForm(this.gridStore.activeBookmark()),
          },
          {
            icon: 'fas fa-plus-circle',
            label: 'Ansicht anlegen',
            command: event => this.showBookmarkForm(),
          },
        ],
      },
    ];
  }

  /**
   * Initializes the top menu by defining the structure and behavior of menu items.
   * The menu items include icons, labels, and associated commands or actions to be executed.
   *
   * @return {void} This method does not return any value.
   */
  private initializeTopMenu(): void {
    this.initializeBookmarkButton();
    this.miscItems = [
      {
        visible: !this.activeUserStore.isLoggedIn(),
        label: 'Login',
        icon: 'fas fa-right-to-bracket',
        target: 'hyperion_login',
        routerLink: ['/login'],
      },
      {
        visible: !!this.activeUserStore.isLoggedIn(),
        label: 'Zur Datenpflege',
        sub: 'Datenpflege in neuem Tab öffnen',
        icon: 'fas fa-database',
        target: 'hyperion_administration',
        routerLink: ['/data-backend'],
      },
      //{
      //  url: 'https://www.youtube.com/playlist?list=PLxyQdUGjPEsF8ZEsi47HHpQRI6-v-8owD',
      //  label: 'Tutorials',
      //  sub: 'YouTube-Kanal in neuem Tab öffnen',
      //  icon: 'fa-brands fa-youtube',
      //},
      {
        visible: !!this.globalStore.activeCommune()?.contactUrl,
        label: 'Kontakt',
        sub: 'Kommunen-Kontakt-Seite in neuem Tab öffnen',
        icon: 'fas fa-at',
        target: 'hyperion_kontakt',
        url: this.globalStore.activeCommune()?.contactUrl,
      },
      {
        label: 'Impressum',
        icon: 'fas fa-legal',
        target: 'hyperion_impressum',
        url: 'https://www.keck-atlas.de/system/impressum',
      },
      {
        label: 'Datenschutz',
        icon: 'fas fa-balance-scale',
        target: 'hyperion_datenschutz',
        url: 'https://www.keck-atlas.de/system/datenschutz',
      },
    ];
  }

  /**
   * Generates and returns an array of menu items based on the bookmarks and stories
   * associated with the given category ID.
   *
   * @param {string} categoryId The identifier for the category to retrieve menu items for.
   * @return {MenuItem[]} An array of menu items containing bookmarks and stories for the given category.
   */
  addMenuChildren(categoryId: string): MenuItem[] {
    const bookmarks = this.groupedBookmarks[categoryId];
    const stories = this.groupedStories[categoryId];
    if (!bookmarks && !stories) return [];
    const items: MenuItem[] = [];

    // adds bookmarks
    bookmarks?.forEach(bookmark => {
      items.push({
        label: bookmark.title,
        icon: bookmark.private ? 'fas fa-eye-slash' : 'fas fa-note-sticky',
        routerLink: ['ansicht', bookmark._id],
      });
    });

    // adds stories
    stories?.forEach(story => {
      items.push({
        label: story.title,
        icon: 'fas fa-file-lines',
        //routerLink: ['bericht', story._id],
        command: (event: MenuItemCommandEvent) => {
          this.globalStore.setActiveStory(story._id);
        },
      });
    });

    return items;
  }

  /**
   * Opens the bookmark form for the user. If a bookmark is provided,
   * the form will be pre-populated for editing that bookmark.
   *
   * @param {Bookmark} [bookmarkToEdit] - An optional bookmark object to edit. If not provided, the form will allow adding a new bookmark.
   * @return {void} Does not return a value.
   */
  showBookmarkForm(bookmarkToEdit?: Bookmark): void {
    this.gridStore.openBookmarkForm(bookmarkToEdit);
  }

  /**
   * This method handles the saving of the current layout to an active bookmark or prompts the user to create a new one.
   * If an active bookmark exists, it confirms overwriting the bookmark with the updated layout.
   * Otherwise, it shows the bookmark creation form.
   *
   * @return {void} No return value.
   */
  quickSave(): void {
    const activeBookmark = this.gridStore.activeBookmark();
    if (!activeBookmark) {
      this.showBookmarkForm();
    } else {
      const layout = this.widgetStore
        .asArray()
        .filter(item => item.type !== 'adder')
        .map(item => this.configService.transformWidgetToSavableItem(item));
      const options = this.gridStore.options();
      this.confirmationDialogService.confirmBookmarkOverwrite(() => {
        const newBookmark = {
          ...activeBookmark,
          ...{ config: layout, layoutOptions: options },
        };
        this.bookmarkStore.update(newBookmark);
        this.gridStore.resetGridHasChanges();
        this.globalStore.triggerAtlasEvent('saveLayout', {
          bookmarkId: newBookmark?._id,
        });
      }, `${activeBookmark.title} wirklich überschreiben?`);
    }
  }

  /**
   * Navigates to the landing page by triggering the appropriate event through the global store.
   *
   * @return {void} This method does not return a value.
   */
  goToLandingpage(): void {
    this.globalStore.closeActiveTopic();
    this.globalStore.triggerAtlasEvent('openLandingPage', {});
  }

  showInfo(): void {
    this.infoVisible = true;
  }
}
